import React from 'react';
import { Table, Card, Tooltip, Avatar, Skeleton } from 'antd';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import cnnaid from '../../../../../../../assets/icons/transactions/cnnaid.png';
import cnnaip from '../../../../../../../assets/icons/transactions/cnnaip.png';
import cnnafingerprint from '../../../../../../../assets/icons/transactions/cnnafingerprint.png';
import { ReactComponent as GlobePhoneIcon } from '../../../../../../../assets/icons/transactions/globephone.svg';
import { DesktopOutlined, MobileOutlined, TableOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import SessionJourneyExpand from './SessionJourneyExpand';
import {
  PrimaryTableRowText,
  TableColumnTitle,
} from '../../../../../../shared/globalStyling/styledText';
import { SESSIONS_TOOLTIP } from '../../../../../../shared/constants/tooltips';

const secondsToTimeText = (seconds) => {
  if (seconds < 0) return 'Invalid time';

  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts = [];

  if (hrs > 0) parts.push(`${hrs}h`);
  if (mins > 0) parts.push(`${mins}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

  return parts.join(' ');
};

const SessionsTable = ({ data = [], loading = false, total }) => {
  const columns = [
    {
      title: <TableColumnTitle text={'Session ID'} />,
      index: 'sessionId',
      key: 'sessionId',
      render: (text, { sessionId }) => {
        return <PrimaryTableRowText text={sessionId} />;
      },
    },
    {
      title: <TableColumnTitle text={'Start Time'} />,
      index: 'sessionStart',
      key: 'sessionStart',
      render: (text, { sessionStart }) => {
        return <PrimaryTableRowText text={moment(sessionStart).format('YYYY-MM-DD HH:mm:ss')} />;
      },
    },
    {
      title: <TableColumnTitle text={'End Time'} />,
      index: 'sessionEnd',
      key: 'sessionEnd',
      render: (text, { sessionEnd }) => {
        return <PrimaryTableRowText text={moment(sessionEnd).format('YYYY-MM-DD HH:mm:ss')} />;
      },
    },
    {
      title: <TableColumnTitle text={'Duration'} />,
      index: 'durationInSeconds',
      key: 'durationInSeconds',
      render: (text, { durationInSeconds }) => {
        return <PrimaryTableRowText text={secondsToTimeText(durationInSeconds)} />;
      },
    },
    {
      title: <TableColumnTitle text={'Page Views'} />,
      index: 'pageViews',
      key: 'pageViews',
      render: (text, { journey }) => {
        return (
          <PrimaryTableRowText
            text={journey.reduce((acc, curr) => {
              if (curr.EVENT === 'page_view') acc += 1;
              return acc;
            }, 0)}
          />
        );
      },
    },
    {
      title: <TableColumnTitle text={'Landing Page'} />,
      index: 'landingPage',
      key: 'landingPage',
      render: (text, { landingPage }) => {
        return <PrimaryTableRowText text={landingPage} />;
      },
    },
    {
      title: <TableColumnTitle text={'Exit Page'} />,
      index: 'exitPage',
      key: 'exitPage',
      render: (text, { exitPage }) => {
        return <PrimaryTableRowText text={exitPage} />;
      },
    },
    {
      title: <TableColumnTitle text={''} />,
      index: 'device',
      key: 'device',
      render: (text, { device }) => {
        const getIcon = () => {
          if (device === 'Mobile') {
            return <MobileOutlined />;
          } else if (device === 'Tablet') {
            return <TableOutlined />;
          }
          return <DesktopOutlined />;
        };
        return (
          <>
            {getIcon()}
            {device}
          </>
        );
      },
    },
    {
      title: <TableColumnTitle text={''} />,
      dataIndex: 'attributionMethod',
      key: 'attributionMethod',
      align: 'left',
      width: '8%',
      render: (col) => {
        let record;

        record = col;
        if (typeof col === 'string') {
          record = JSON.parse(col);
        }

        let method = '';
        let attributionIcon = cnnaip;
        let tip = '';
        //Single object with 1 key
        if (record) {
          method = Object.keys(record)[0];
          if (method === 'COOKIE') {
            attributionIcon = cnnaid;
            tip = 'Cookie Match - Deterministic match using browser based cookie / uuid match.';
          } else if (method === 'FINGERPRINT') {
            attributionIcon = cnnafingerprint;
            tip =
              'User Match - Probabilistic match using user fingerprint. (User fingerprint - The combination of user agent and IP address to produce a unique hash)';
          } else if (method === 'DEVICE_FINGERPRINT') {
            tip =
              'Device Match - Probabilistic match using user device model information (Device model, OS, OS version ) combined with IP address';
          } else if (method === 'IP') {
            tip = 'IP Match - Probabilistic match using IP address.';
            attributionIcon = cnnaip;
          }
        }
        const attributionIcn = () => {
          if (method === 'DEVICE_FINGERPRINT') {
            return (
              <span role="img" className="anticon" style={{ verticalAlign: '-5px' }}>
                <GlobePhoneIcon
                  height="35"
                  width="35"
                  style={{
                    opacity: '1.0',
                  }}
                />
              </span>
            );
          } else {
            return <Avatar src={attributionIcon} />;
          }
        };
        return <Tooltip title={tip}>{attributionIcn()}</Tooltip>;
      },
    },
  ];
  return (
    <React.Fragment>
      <Card
        title={
          <span>
            Sessions{' '}
            <Tooltip title={SESSIONS_TOOLTIP}>
              <QuestionCircleOutlined style={{ color: '#1890ff', marginLeft: '4px' }} />
            </Tooltip>
          </span>
        }
      >
        {loading ? (
          <Skeleton active />
        ) : (
          <>
            <Table
              dataSource={data}
              columns={columns}
              pagination={{
                position: ['none', 'bottomCenter'],
                total: total ? total : data.length,
                responsive: true,
                showSizeChanger: false,
                showLessItems: true,
                size: 'default',
              }}
              rowKey={({ sessionId }) => sessionId}
              scroll={{ x: 1200, y: 1700 }}
              expandedRowRender={(record) => {
                const { impressionsList, journey } = record;
                return <SessionJourneyExpand impressions={impressionsList} journey={journey} />;
              }}
            />
          </>
        )}
      </Card>
    </React.Fragment>
  );
};

export default withRouter(SessionsTable);
